import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'

import { getCollectionCacheKey } from './utils'

interface UseCollectionCacheProps {
  pathname: string
  searchParams: string
}

const useCollectionCache = ({ pathname, searchParams }: UseCollectionCacheProps) => {
  // cacheKey is the key for the collection cache
  const cacheKey = getCollectionCacheKey(pathname, searchParams)

  const router = useRouter()

  const {
    page: pageCache,
    hasPaginationTerminated: hasPaginationTerminatedCache,
    scrollY: scrollYCache,
    collectionPage,
  } = collectionCacheStateManager.getCollectionCache(cacheKey) ?? {}

  // currentCacheKey is the key for the current collection cache
  const currentCacheKey = useRef(cacheKey)

  // scrollY is the scroll position of the page
  const scrollY = useRef(0)

  useEffect(() => {
    // update the current cache key when the cache key changes
    currentCacheKey.current = cacheKey
  }, [cacheKey])

  // scroll to the scroll position of the page when the pathname or search params change
  useEffect(() => {
    if (scrollYCache) {
      setTimeout(() => {
        window.scrollTo(0, scrollYCache)
      }, 10)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams])

  useEffect(() => {
    // Store the scroll position of the page
    const handleScroll = () => {
      scrollY.current = window.scrollY
    }

    // Store the scroll position of the page in the collection cache when the route changes
    const handleRouteChange = () => {
      collectionCacheStateManager.publishCollectionCache(currentCacheKey.current, scrollY.current)
    }

    // Add the event listener for the scroll event to store the scroll position
    window.addEventListener('scroll', handleScroll, { passive: true })
    // Add the event listener for the route change event to store the scroll position in the collection cache
    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      // Remove the event listener for the scroll event
      window.removeEventListener('scroll', handleScroll)
      // Remove the event listener for the route change event
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  const getNextFeedURL = () => {
    if (!collectionPage) return undefined
    if ('nextFeedURL' in collectionPage) return collectionPage.nextFeedURL
    if ('next_url' in collectionPage) return collectionPage.next_url
    return undefined
  }

  return {
    pageCache,
    hasPaginationTerminatedCache,
    currentCacheKey,
    scrollY,
    nextFeedURLCache: getNextFeedURL(),
    collectionPage,
  }
}

export default useCollectionCache
